import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
    BSKT_Diem_ViewModel,
    BSKT_TongHopDiemSinhVien,
} from "../../services/diem/DiemModels";
import { BSKT_GetTreeView_DotTuyenSinh_Nganh } from "../../services/chuyen-nganh/ChuyenNganhModels";

type DiemEditableField = "Diem_thi";
type TongHopEditableField = "Diem_Thi";

export interface BSKT_DiemSelected {
    ID_diem: number;
    ID_thi_sinh: number;
}

export interface DiemLockStatus {
    is_locked: boolean;
    pending_unlock_request: boolean;
    ly_do_pending?: string | null;
    created_at_pending?: string | null;
}

interface DiemState {
    danhSachDiem: BSKT_Diem_ViewModel[];
    danhSachTongHopDiem: BSKT_TongHopDiemSinhVien[];

    selectedLopId: number | null;
    selectedDiemId: number | null;
    selectedDotNganhItem: BSKT_GetTreeView_DotTuyenSinh_Nganh | null;

    isShowConfirmDeleteModal: boolean;
    status: "idle" | "loading" | "succeeded" | "failed";
    selectedDiemIDs: BSKT_DiemSelected[];

    // LOCK STATE
    lockStatus: DiemLockStatus;
    lockStatusLoading: boolean;
}

const initialState: DiemState = {
    danhSachDiem: [],
    danhSachTongHopDiem: [],

    selectedLopId: null,
    selectedDiemId: null,
    selectedDotNganhItem: null,

    isShowConfirmDeleteModal: false,
    selectedDiemIDs: [] as BSKT_DiemSelected[],
    status: "idle",

    lockStatus: {
        is_locked: false,
        pending_unlock_request: false,
        ly_do_pending: null,
        created_at_pending: null,
    },
    lockStatusLoading: false,
};

const diemSlice = createSlice({
    name: "diem",
    initialState,
    reducers: {
        setDanhSachDiem: (state, action: PayloadAction<BSKT_Diem_ViewModel[]>) => {
            state.danhSachDiem = action.payload;
            state.status = "succeeded";
        },

        setTongHopDiem: (
            state,
            action: PayloadAction<BSKT_TongHopDiemSinhVien[]>
        ) => {
            state.danhSachTongHopDiem = action.payload;
        },

        // ====== NH?P ?I?M THEO L?P (danhSachDiem) ======
        themDiem: (state, action: PayloadAction<BSKT_Diem_ViewModel>) => {
            const index = state.danhSachDiem.findIndex(
                (d) =>
                    d.ID_diem === action.payload.ID_diem &&
                    d.ID_thi_sinh === action.payload.ID_thi_sinh &&
                    d.ID_mon === action.payload.ID_mon
            );

            if (index !== -1) {
                state.danhSachDiem[index] = {
                    ...state.danhSachDiem[index],
                    Diem_thi: action.payload.Diem_thi ?? null,
                };
            } else {
                state.danhSachDiem.push(action.payload);
            }
        },

        capNhatDiem: (state, action: PayloadAction<BSKT_Diem_ViewModel>) => {
            const index = state.danhSachDiem.findIndex(
                (d) =>
                    d.ID_diem === action.payload.ID_diem &&
                    d.ID_thi_sinh === action.payload.ID_thi_sinh &&
                    d.ID_mon === action.payload.ID_mon
            );
            if (index !== -1) {
                state.danhSachDiem[index] = {
                    ...state.danhSachDiem[index],
                    Diem_thi: action.payload.Diem_thi ?? null,
                };
            }
        },

        setDiemChoThiSinh: (
            state,
            action: PayloadAction<{
                ID_diem: number;
                ID_thi_sinh: number;
                ID_mon: number;
                ID_lop_bskt?: number;
                field: DiemEditableField;
                value: number | null;
            }>
        ) => {
            const { ID_diem, ID_thi_sinh, ID_mon, ID_lop_bskt, field, value } =
                action.payload;

            const index = state.danhSachDiem.findIndex((d: any) => {
                const dId = Number(d.ID_diem ?? 0);
                if (ID_diem > 0 && dId > 0) return dId === ID_diem;

                const sameThiSinh =
                    Number(d.ID_thi_sinh ?? 0) === Number(ID_thi_sinh ?? 0);
                const sameMon = Number(d.ID_mon ?? 0) === Number(ID_mon ?? 0);

                if (ID_lop_bskt != null) {
                    const sameLop =
                        Number(d.ID_lop_bskt ?? 0) === Number(ID_lop_bskt ?? 0);
                    return sameThiSinh && sameMon && sameLop;
                }
                return sameThiSinh && sameMon;
            });

            if (index !== -1) {
                (state.danhSachDiem[index] as any)[field] = value;
            }
        },

        xoaDiem: (state, action: PayloadAction<number>) => {
            state.danhSachDiem = state.danhSachDiem.filter(
                (d) => d.ID_diem !== action.payload
            );
            state.isShowConfirmDeleteModal = false;
        },

        toggleConfirmDeleteModal: (state, action: PayloadAction<number | null>) => {
            state.isShowConfirmDeleteModal = !state.isShowConfirmDeleteModal;
            state.selectedDiemId = action.payload;
        },

        setStatus: (state, action: PayloadAction<DiemState["status"]>) => {
            state.status = action.payload;
        },

        setSelectedDiemId: (state, action: PayloadAction<number>) => {
            state.selectedDiemId = action.payload;
        },

        setSelectedLopId: (state, action: PayloadAction<number>) => {
            state.selectedLopId = action.payload;
        },

        setSelectedDotNganhItem: (
            state,
            action: PayloadAction<BSKT_GetTreeView_DotTuyenSinh_Nganh | null>
        ) => {
            state.selectedDotNganhItem = action.payload;
        },

        chonDiem: (state, action: PayloadAction<BSKT_DiemSelected>) => {
            const exists = state.selectedDiemIDs.some(
                (item) =>
                    item.ID_diem === action.payload.ID_diem &&
                    item.ID_thi_sinh === action.payload.ID_thi_sinh
            );
            if (!exists) state.selectedDiemIDs.push(action.payload);
        },

        boChonDiem: (state, action: PayloadAction<BSKT_DiemSelected>) => {
            state.selectedDiemIDs = state.selectedDiemIDs.filter(
                (item) =>
                    !(
                        item.ID_diem === action.payload.ID_diem &&
                        item.ID_thi_sinh === action.payload.ID_thi_sinh
                    )
            );
        },

        // ====== T?NG H?P (danhSachTongHopDiem[].ListDiem[]) ======
        setTongHopDiemChoThiSinh: (
            state,
            action: PayloadAction<{
                ID_thi_sinh: number;
                ID_mon: number;
                ID_diem: number;
                field: TongHopEditableField;
                value: number | null;
            }>
        ) => {
            const { ID_thi_sinh, ID_mon, ID_diem, field, value } = action.payload;

            const sv = state.danhSachTongHopDiem.find(
                (x: any) => Number(x?.ID_thi_sinh ?? 0) === Number(ID_thi_sinh ?? 0)
            );
            if (!sv) return;

            const list: any[] = (sv as any).ListDiem ?? [];

            const idx = list.findIndex((d: any) => {
                const dId = Number(d?.ID_diem ?? 0);
                if (ID_diem > 0 && dId > 0) return dId === ID_diem;
                return Number(d?.ID_mon ?? 0) === Number(ID_mon ?? 0);
            });

            if (idx !== -1) {
                list[idx][field] = value;
            }
        },

        // ===== LOCK reducers =====
        setLockStatusLoading: (state, action: PayloadAction<boolean>) => {
            state.lockStatusLoading = action.payload;
        },

        setLockStatus: (state, action: PayloadAction<DiemLockStatus>) => {
            state.lockStatus = action.payload;
        },

        resetLockStatus: (state) => {
            state.lockStatus = {
                is_locked: false,
                pending_unlock_request: false,
                ly_do_pending: null,
                created_at_pending: null,
            };
        },

        resetDiemState: () => initialState,
    },
});

export const {
    setDanhSachDiem,
    themDiem,
    capNhatDiem,
    xoaDiem,
    setDiemChoThiSinh,
    toggleConfirmDeleteModal,
    setTongHopDiem,
    setTongHopDiemChoThiSinh,
    setStatus,
    setSelectedDiemId,
    setSelectedLopId,
    resetDiemState,
    setSelectedDotNganhItem,
    boChonDiem,
    chonDiem,

    setLockStatus,
    setLockStatusLoading,
    resetLockStatus,
} = diemSlice.actions;

export const DiemReducer = diemSlice.reducer;